import { Vector3, Quaternion } from 'three'
import { FieldParameters } from '../field/FieldParameters'
import { PhaseUtils } from '../../math/PhaseUtils'
import { FieldAnimationState } from './POIFieldInterface'
import { HoverDirection } from '../field/FieldTypes'

export class POIAnimation {
  // ==================== ANIMATION STATE ====================
  
  private _animationState: FieldAnimationState = FieldAnimationState.Static
  private _lastUpdateTime: number = 0
  private _animationStartTime: number = 0
  
  // Animation history for smoothing
  private _positionHistory: Vector3[] = []
  private _rotationHistory: Quaternion[] = []
  private _scaleHistory: number[] = []
  
  // ==================== HOVER ANIMATION ====================
  
  /**
   * Compute hover animation offset
   */
  public computeHover(
    poi: any,
    params: FieldParameters,
    deltaTime: number
  ): Vector3 {
    const time = params.time
    const phaseShift = (poi.index / poi.totalCount) * Math.PI * 2 + params.hoverPhaseShift
    
    // Calculate base hover wave
    const hoverWave = PhaseUtils.applyWave(
      params.hoverAmplitude,
      params.hoverFrequency,
      time,
      phaseShift
    )
    
    // Apply directional hover
    const hoverDirection = this.getHoverDirection(poi, params)
    const hoverOffset = hoverDirection.multiplyScalar(hoverWave)
    
    // Add organic variation
    if (params.organicVariation > 0) {
      const organicOffset = this.computeOrganicHover(poi, params, time)
      hoverOffset.add(organicOffset)
    }
    
    // Store in history for smoothing
    this._positionHistory.push(hoverOffset.clone())
    if (this._positionHistory.length > 5) {
      this._positionHistory.shift()
    }
    
    return this.smoothPosition(hoverOffset)
  }
  
  /**
   * Get hover direction based on field topology
   */
  private getHoverDirection(poi: any, params: FieldParameters): Vector3 {
    // This would normally use the field context to determine direction
    // For now, use a simple default
    return new Vector3(0, 1, 0) // Default: hover up/down
  }
  
  /**
   * Compute organic hover variation
   */
  private computeOrganicHover(poi: any, params: FieldParameters, time: number): Vector3 {
    const organicPhase = PhaseUtils.createOrganicPhaseShift(
      params.hoverFrequency * 0.3,
      time,
      params.organicVariation
    )
    
    // Create subtle 3D movement
    const x = Math.sin(time * 0.7 + poi.index * 0.1) * params.organicVariation * 0.5
    const y = Math.cos(time * 0.5 + poi.index * 0.15) * params.organicVariation * 0.3
    const z = Math.sin(time * 0.9 + poi.index * 0.05) * params.organicVariation * 0.4
    
    return new Vector3(x, y, z)
  }
  
  // ==================== ROTATION ANIMATION ====================
  
  /**
   * Compute rotation animation offset
   */
  public computeRotation(
    poi: any,
    params: FieldParameters,
    deltaTime: number
  ): Quaternion {
    const time = params.time
    const phaseShift = (poi.index / poi.totalCount) * Math.PI * 2 + params.rotatePhaseShift
    
    // Calculate base rotation wave
    const rotationAngle = PhaseUtils.applyWave(
      params.rotateAmplitude,
      params.rotateFrequency,
      time,
      phaseShift
    )
    
    // Apply rotation around up axis
    const rotationAxis = params.customUpVector.clone().normalize()
    const rotationOffset = new Quaternion()
    rotationOffset.setFromAxisAngle(rotationAxis, rotationAngle)
    
    // Add wind effect
    if (params.windStrength > 0) {
      const windRotation = this.computeWindRotation(poi, params, time)
      rotationOffset.multiply(windRotation)
    }
    
    // Store in history for smoothing
    this._rotationHistory.push(rotationOffset.clone())
    if (this._rotationHistory.length > 5) {
      this._rotationHistory.shift()
    }
    
    return this.smoothRotation(rotationOffset)
  }
  
  /**
   * Compute wind effect rotation
   */
  private computeWindRotation(poi: any, params: FieldParameters, time: number): Quaternion {
    const windPhase = poi.index * 0.1 + time * params.waveSpeed
    const windAngle = Math.sin(windPhase) * params.windStrength
    
    const windAxis = params.customAlignmentVector.clone().normalize()
    const windRotation = new Quaternion()
    windRotation.setFromAxisAngle(windAxis, windAngle)
    
    return windRotation
  }
  
  // ==================== SCALE ANIMATION ====================
  
  /**
   * Compute scale animation offset
   */
  public computeScale(
    poi: any,
    params: FieldParameters,
    deltaTime: number
  ): number {
    const time = params.time
    const phaseShift = (poi.index / poi.totalCount) * Math.PI * 2 + params.hoverPhaseShift
    
    // Calculate base scale wave
    const scaleWave = PhaseUtils.applyWave(
      params.scaleAmplitude,
      params.hoverFrequency,
      time,
      phaseShift
    )
    
    // Convert to scale factor (1.0 + wave)
    const scaleOffset = 1.0 + scaleWave
    
    // Apply breathing effect
    if (params.breathingIntensity > 0) {
      const breathingScale = this.computeBreathingScale(poi, params, time)
      return scaleOffset * breathingScale
    }
    
    // Store in history for smoothing
    this._scaleHistory.push(scaleOffset)
    if (this._scaleHistory.length > 5) {
      this._scaleHistory.shift()
    }
    
    return this.smoothScale(scaleOffset)
  }
  
  /**
   * Compute breathing effect scale
   */
  private computeBreathingScale(poi: any, params: FieldParameters, time: number): number {
    // Synchronized breathing - all POIs breathe together
    const breathingWave = PhaseUtils.createBreathingEffect(
      params.breathingIntensity * 0.2,
      params.hoverFrequency * 0.5,
      time
    )
    
    return 1.0 + breathingWave
  }
  
  // ==================== COMBINED ANIMATIONS ====================
  
  /**
   * Compute all animations at once
   */
  public computeAllAnimations(
    poi: any,
    params: FieldParameters,
    deltaTime: number
  ): {
    hover: Vector3
    rotation: Quaternion
    scale: number
  } {
    return {
      hover: this.computeHover(poi, params, deltaTime),
      rotation: this.computeRotation(poi, params, deltaTime),
      scale: this.computeScale(poi, params, deltaTime)
    }
  }
  
  // ==================== ANIMATION SMOOTHING ====================
  
  /**
   * Smooth position using history
   */
  private smoothPosition(position: Vector3): Vector3 {
    if (this._positionHistory.length < 2) return position
    
    const smoothed = new Vector3()
    const weights = [0.4, 0.3, 0.2, 0.1] // Recent positions have higher weight
    
    for (let i = 0; i < Math.min(this._positionHistory.length, weights.length); i++) {
      const historyIndex = this._positionHistory.length - 1 - i
      const weight = weights[i]
      smoothed.add(this._positionHistory[historyIndex].clone().multiplyScalar(weight))
    }
    
    return smoothed
  }
  
  /**
   * Smooth rotation using history
   */
  private smoothRotation(rotation: Quaternion): Quaternion {
    if (this._rotationHistory.length < 2) return rotation
    
    let smoothed = rotation.clone()
    const alpha = 0.7 // Smoothing factor
    
    for (let i = this._rotationHistory.length - 2; i >= 0; i--) {
      smoothed.slerp(this._rotationHistory[i], alpha * (i + 1) / this._rotationHistory.length)
    }
    
    return smoothed
  }
  
  /**
   * Smooth scale using history
   */
  private smoothScale(scale: number): number {
    if (this._scaleHistory.length < 2) return scale
    
    let smoothed = scale
    const alpha = 0.7 // Smoothing factor
    
    for (let i = this._scaleHistory.length - 2; i >= 0; i--) {
      smoothed = smoothed * alpha + this._scaleHistory[i] * (1 - alpha)
    }
    
    return smoothed
  }
  
  // ==================== ANIMATION STATES ====================
  
  /**
   * Set animation state
   */
  public setAnimationState(state: FieldAnimationState): void {
    if (this._animationState !== state) {
      this._animationState = state
      this._animationStartTime = Date.now()
    }
  }
  
  /**
   * Get current animation state
   */
  public getAnimationState(): FieldAnimationState {
    return this._animationState
  }
  
  /**
   * Get animation duration
   */
  public getAnimationDuration(): number {
    return Date.now() - this._animationStartTime
  }
  
  // ==================== PRESET ANIMATIONS ====================
  
  /**
   * Apply breathing preset
   */
  public applyBreathingPreset(poi: any, params: FieldParameters): void {
    this.setAnimationState(FieldAnimationState.Breathing)
    
    // Configure parameters for breathing
    params.hoverDirection = HoverDirection.Transversal
    params.hoverAmplitude = 1.5
    params.hoverFrequency = 0.8
    params.breathingIntensity = 1.0
    params.hoverPhaseShift = 0 // Synchronized
  }
  
  /**
   * Apply flowing preset
   */
  public applyFlowingPreset(poi: any, params: FieldParameters): void {
    this.setAnimationState(FieldAnimationState.Flowing)
    
    // Configure parameters for flowing
    params.hoverDirection = HoverDirection.Longitudinal
    params.hoverAmplitude = 2.0
    params.hoverFrequency = 1.2
    params.waveSpeed = 2.0
    params.hoverPhaseShift = Math.PI / 3 // Progressive wave
  }
  
  /**
   * Apply rippling preset
   */
  public applyRipplingPreset(poi: any, params: FieldParameters): void {
    this.setAnimationState(FieldAnimationState.Rippling)
    
    // Configure parameters for rippling
    params.hoverDirection = HoverDirection.Transversal
    params.hoverAmplitude = 1.8
    params.rippleFrequency = 2.5
    params.waveSpeed = 1.5
    // Phase shift will be calculated based on distance from center
  }
  
  // ==================== UTILITY METHODS ====================
  
  /**
   * Reset animation history
   */
  public resetHistory(): void {
    this._positionHistory = []
    this._rotationHistory = []
    this._scaleHistory = []
  }
  
  /**
   * Update timing
   */
  public updateTiming(deltaTime: number): void {
    this._lastUpdateTime += deltaTime
  }
  
  /**
   * Get smooth update rate
   */
  public getUpdateRate(): number {
    return this._lastUpdateTime > 0 ? 1 / this._lastUpdateTime : 0
  }
} 